import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import type { ReactNode } from 'react'

type Role = 'admin' | 'super_admin' | 'council' | 'talent' | 'member'

interface RoleGuardProps {
  /** Roles allowed to access this route */
  roles: Role[]
  allowReadAll?: boolean
  children: ReactNode
}

/**
 * Client-side route guard that checks the effective user's role (respecting
 * impersonation) against an allow-list. Unauthorized users are redirected to
 * "/" with a location state flag so AppLayout can display a feedback message.
 *
 * Usage:
 *   <Route path="users" element={<RoleGuard roles={['admin','super_admin']}><UserManagement /></RoleGuard>} />
 */
export function RoleGuard({ roles, allowReadAll = false, children }: RoleGuardProps) {
  const { user } = useAuth()
  const effectiveUser = useEffectiveUser()
  const location = useLocation()

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const effectiveRole = (effectiveUser?.role ?? user.role) as Role
  if (!roles.includes(effectiveRole) && !(allowReadAll && effectiveUser?.views_all_demands)) {
    return (
      <Navigate
        to="/"
        replace
        state={{ accessDenied: true, from: location.pathname }}
      />
    )
  }

  return <>{children}</>
}
